import { createReducer, on } from '@ngrx/store';

import * as fromModuleModels from '../../models';
import { CustomersActions } from '../actions';
import { ErrorsObject } from '../../../shared/utilities/error-utility.utility';
import { getAndHandleBackendErrors } from '../../../shared/utilities/reducer.utility';
import { UserRoleActions } from '../../../application-state/store/actions';

export interface State {
  current: fromModuleModels.Customer;
  currentPartner: fromModuleModels.Customer;
  entities: { [iri: string]: fromModuleModels.Customer };
  errors: ErrorsObject;
  loaded: boolean;
  loading: boolean;
  pagination?: {
    current?: string;
    first?: string;
    last?: string;
    next?: string;
    previous?: string;
  };
  totalItems: number;
  searchEntities: Array<string>;
}

const initialState = {
  current: null,
  currentPartner: null,
  entities: {},
  errors: {},
  loaded: false,
  loading: false,
  searchEntities: [],
  totalItems: 0
};

export const reducer = createReducer(
  initialState,
  on(
    CustomersActions.CreateCustomer,
    CustomersActions.UpdateCustomer,
    CustomersActions.DeleteCustomer,
    CustomersActions.ReadCustomers,
    CustomersActions.ReadCustomerChunk,
    state => {
      return {
        ...state,
        loading: true,
        loaded: false
      };
    }
  ),
  on(CustomersActions.CreateCustomerSuccess, (state, { response }) => {
    const entities = {
      [response['@id']]: response,
      ...state.entities
    };

    return {
      ...state,
      entities,
      searchEntities: [response['@id']],
      totalItems: state.totalItems + 1,
      loading: false
    };
  }),
  on(CustomersActions.ReadCustomerSuccess, (state, { response }) => {
    const entities = {
      ...state.entities,
      [response['@id']]: response
    };

    return {
      ...state,
      current: response,
      entities,
      loading: false,
      searchEntities: [response['@id']]
    };
  }),
  on(CustomersActions.ReadPartnerSuccess, (state, { response }) => {
    const entities = {
      ...state.entities,
      [response['@id']]: response
    };

    return {
      ...state,
      currentPartner: response,
      entities,
      loading: false
      // searchEntities: [response['@id']],
    };
  }),
  on(
    CustomersActions.ReadCustomersSuccess,
    CustomersActions.ReadCustomerChunkSuccess,
    (state, { response }) => {
      const hasPagination = response.hasOwnProperty('hydra:view');
      const items = response['hydra:member'];
      const totalItems = response['hydra:totalItems'];
      let pagination = {};
      let searchEntities = [];

      const entities = items.reduce(
        (entities: { [id: string]: fromModuleModels.Customer }, item) => {
          return {
            ...entities,
            [item['@id']]: item
          };
        },
        // intentionally "reset" entities for proper pagination as opposed to accumulating values
        // todo: local caching? maybe use custom MatDataSource?
        {}
        // {...state.entities}
      );

      if (response.hasQueryParam) {
        items.forEach(item => {
          searchEntities = [...searchEntities, item['@id']];
        });
      }

      // Sample
      // "@id": "/api/customers?page=1",                          ---- always available
      // "hydra:first": "/api/customers?page=1",                  ---- always available
      // "hydra:next": "/api/customers?page=2"                    ---- might be unavailable
      // "hydra:previous": "/api/customers?page=2"                ---- might be unavailable
      // "hydra:last": "/api/customers?page=2",                   ---- always available

      if (hasPagination) {
        pagination = {
          first: response['hydra:view']['hydra:first'],
          current: response['hydra:view']['@id'],
          last: response['hydra:view']['hydra:last']
        };

        if (response['hydra:view'].hasOwnProperty('hydra:previous')) {
          pagination = {
            ...pagination,
            previous: response['hydra:view']['hydra:previous']
          };
        }

        if (response['hydra:view'].hasOwnProperty('hydra:next')) {
          pagination = {
            ...pagination,
            next: response['hydra:view']['hydra:next']
          };
        }
      }

      return {
        ...state,
        entities,
        searchEntities,
        totalItems,
        pagination,
        loading: false,
        loaded: totalItems.length === items.length
      };
    }
  ),
  on(
    CustomersActions.UpdateCustomerSuccess,
    CustomersActions.SetDefaultCustomerContactSuccess,
    CustomersActions.SetDefaultCustomerAddressSuccess,
    (state, { response }) => {
      const entities = {
        ...state.entities,
        [response['@id']]: response
      };

      return {
        ...state,
        entities,
        current: response,
        loading: false
      };
    }
  ),
  on(CustomersActions.DeleteCustomerSuccess, (state, { iri }) => {
    // @ts-ignore
    const { [iri]: removedItem, ...entities } = state.entities;

    return {
      ...state,
      entities,
      errors: {},
      loading: false
    };
  }),
  on(
    CustomersActions.CreateCustomerFail,
    CustomersActions.ReadCustomerFail,
    CustomersActions.ReadCustomersFail,
    CustomersActions.UpdateCustomerFail,
    (state, { type, response }) => {
      return {
        ...state,
        errors: getAndHandleBackendErrors(response),
        loading: false
      };
    }
  ),
  on(CustomersActions.ResetCurrentCustomer, state => {
    return {
      ...state,
      current: null
    };
  }),
  on(CustomersActions.ResetIsLoaded, state => {
    return {
      ...state,
      loaded: false
    };
  }),
  on(UserRoleActions.UserRoleCheckFail, state => ({ ...state, loading: false }))
);
